import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent
} from '@mui/material';
import Box from '@mui/material/Box';
import { Fragment, useEffect, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { Asset } from '../../../../store/Asset';
import { handleCommissionsInfoValidation as validateAssetCommissionInfo } from '../../../../store/Asset';
import { ProvisionalAsset } from '../../../../store/ProvisionalAsset';
import { handleCommissionsInfoValidation as validateProvisionalAssetCommissionInfo } from '../../../../store/ProvisionalAsset';
import { assetCommissionsStatusOptions } from '../../../../store/GeneralStore';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { Text } from '../../../Atoms/Text';
import { getDecimal, getFormattedDate, getUTCRunDate } from '../../../../utils/helpers';
import FinancialInputField from './FinancialInputField';
import { CustomDatePicker } from '../CustomDatePicker';
import { SelectEditComponent } from '../Inputs/SelectEditComponent';
import { CallResult } from '../../../../Api/APIService';
import { FieldError } from './FieldError';
import {
  FinancialEditPayload,
  FINANCIAL_FIELD_NAMES,
  FinancialFields,
  FormDataState,
  REVENUE_CHANGE_CATEGORY_FIELDS,
  allKeys,
  buildSavePayload,
  getChangedFields,
  isBlank,
  toServerErrors
} from './financialEditHelpers';

interface EditFinancialValuesDialogProps {
  open: boolean;
  onClose: () => void;
  data: Asset | ProvisionalAsset;
  onSave?: (data: FinancialEditPayload) => Promise<CallResult<unknown> | void> | void;
  type?: 'asset' | 'provisionalAsset';
}

const REVENUE_CHANGE_CATEGORY_OPTIONS = ['Current', 'Historical Cleanup'];

const CATEGORY_REQUIRED_MESSAGE = 'Revenue Change Category is required.';
const SAVE_FAILED_MESSAGE = 'Failed to save the changes. Please try again.';

const RUN_DATE_FIELDS = [
  ['actualFirstCommissionRun', 'Actual First Commission Run'],
  ['actualDecommissionedDate', 'Actual Decommission Run'],
  ['actualFirstSpiffRun', 'Actual First SPIFF Run']
] as const;

const formatMonthYear = (value: string | null) => getFormattedDate(value, 'MMM YYYY');

export const EditFinancialValuesDialog = ({
  open,
  onClose,
  data,
  onSave,
  type = 'asset'
}: EditFinancialValuesDialogProps) => {
  const [split, setSplit] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>();
  const [formData, setFormData] = useState<FormDataState>({} as FormDataState);
  const formDataRef = useRef<FormDataState>({} as FormDataState);
  const [initialFormData, setInitialFormData] = useState<FormDataState>({} as FormDataState);
  const [storedFinancialData, setStoredFinancialData] = useState<FinancialFields>({} as FinancialFields);
  const [revenueChangeCategory, setRevenueChangeCategory] = useState('');
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const keys = allKeys[type as keyof typeof allKeys];

  // expectedFirstCommissionRun/expectedDecommissionDate are read-only and sourced
  // directly from `data` -- never part of editable form state.
  const readOnlyDate = (field: 'expectedFirstCommissionRun' | 'expectedDecommissionDate') => {
    const mappedKey = keys[field];
    return mappedKey ? ((data[mappedKey as keyof typeof data] as string) ?? null) : null;
  };

  const updateFormData = (next: FormDataState) => {
    formDataRef.current = next;
    setFormData(next);
  };

  const handleFieldChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrors(undefined);
    const val = Number(Number(e.target.value).toFixed(2));
    const amount = Number(val);
    const isClearedBlank = e.target.value === '' && isBlank(initialFormData[field as keyof FormDataState]);
    const fieldValue = isClearedBlank ? null : val;

    switch (field as keyof typeof formData) {
      case 'totalMRC': {
        if (amount < Number(formData?.commissionableMRC)) {
          setErrors({ [field]: 'Total MRC cannot be less than Commissionable MRC' });
        }
        updateFormData({
          ...formData,
          [field]: fieldValue
        });
        break;
      }
      case 'commissionableMRC': {
        const grossMRR = getDecimal(amount * (Number(formData?.recurringCommissionPercent) / 100));
        if (formData?.totalMRC && amount > Number(formData?.totalMRC)) {
          setErrors({ totalMRC: 'Total MRC cannot be less than Commissionable MRC' });
        }
        updateFormData({
          ...formData,
          grossMRR,
          netMRR: getDecimal(Number(grossMRR) * (split / 100)),
          [field]: fieldValue
        });
        break;
      }
      case 'recurringCommissionPercent': {
        const grossMRR = getDecimal((amount / 100) * Number(formData?.commissionableMRC));
        updateFormData({
          ...formData,
          grossMRR,
          netMRR: getDecimal(Number(grossMRR) * (split / 100)),
          [field]: fieldValue
        });
        break;
      }
      case 'commissionableNRC': {
        const grossNRR = getDecimal(amount * (Number(formData?.nonRecurringCommissionPercent) / 100));
        updateFormData({
          ...formData,
          grossNRR,
          netNRR: getDecimal(Number(grossNRR) * (split / 100)),
          [field]: fieldValue
        });
        break;
      }
      case 'nonRecurringCommissionPercent': {
        const grossNRR = getDecimal((amount / 100) * Number(formData?.commissionableNRC));
        updateFormData({
          ...formData,
          grossNRR,
          netNRR: getDecimal(Number(grossNRR) * (split / 100)),
          [field]: fieldValue
        });
        break;
      }
      default:
        updateFormData({
          ...formData,
          [field]: fieldValue
        });
        break;
    }
  };

  const handleCommissionDateChange =
    (field: 'actualFirstCommissionRun' | 'actualFirstSpiffRun' | 'actualDecommissionedDate') => (date: Date | null) => {
      setErrors(undefined);
      const parsed = date && date.toString() !== 'Invalid Date' ? getUTCRunDate(date) : null;
      const newValue = parsed ? parsed.toISOString() : null;

      const updated: FormDataState = { ...formData, [field]: newValue };
      // Setting an Actual Decommission Run date implies the asset is now
      // decommissioned, unless it's already in the terminal Legacy Archive state.
      if (field === 'actualDecommissionedDate' && newValue && newValue !== formData.actualDecommissionedDate) {
        if (formData.status !== 'Legacy Archive') {
          updated.status = 'Decommissioned';
        }
      }
      updateFormData(updated);
    };

  const handleRevenueChangeCategoryChange = (e: SelectChangeEvent) => {
    setErrors(undefined);
    setRevenueChangeCategory(e.target.value);
  };

  const validateCommissionInfo = (current: FormDataState) => {
    const candidate = {
      ...data,
      [keys.status]: current.status,
      [keys.actualDecommissionedDate]: current.actualDecommissionedDate
    };
    return type === 'asset'
      ? validateAssetCommissionInfo(candidate as Asset)
      : validateProvisionalAssetCommissionInfo(candidate as ProvisionalAsset);
  };

  const changedFields = getChangedFields(formData, initialFormData, storedFinancialData);
  const isCategoryRequired = changedFields.some((field) => REVENUE_CHANGE_CATEGORY_FIELDS.includes(field));
  const showCategoryRequired = isCategoryRequired || !!errors?.revenueChangeCategory;

  const handleSave = async () => {
    if (!onSave) return;

    const currentFormData = formDataRef.current;
    const currentChangedFields = getChangedFields(currentFormData, initialFormData, storedFinancialData);
    const currentCategoryRequired = currentChangedFields.some((field) =>
      REVENUE_CHANGE_CATEGORY_FIELDS.includes(field)
    );

    const commissionInfoValidation = validateCommissionInfo(currentFormData);
    const validationErrors: { [key: string]: string } = Object.fromEntries(
      commissionInfoValidation.messages.map((m) => [m.field, m.message])
    );
    if (currentCategoryRequired && !revenueChangeCategory)
      validationErrors.revenueChangeCategory = CATEGORY_REQUIRED_MESSAGE;
    if (Object.keys(validationErrors).length) {
      setErrors((previous) => ({ ...previous, ...validationErrors }));
      setSubmitAttempts((attempts) => attempts + 1);
      return;
    }

    if (!currentChangedFields.length) {
      onClose();
      return;
    }

    setErrors(undefined);
    setLoading(true);
    let saveErrorMessage: string | undefined;
    try {
      const saveOutcome = await onSave(
        buildSavePayload({
          type,
          data,
          keys,
          formData: currentFormData,
          initialFormData,
          changedFields: currentChangedFields,
          revenueChangeCategory
        })
      );
      saveErrorMessage = saveOutcome?.error?.message;
      if (saveOutcome?.error) {
        datadogLogs.logger.error('Edit Values save returned an error', {
          message: saveOutcome.error.message,
          status: saveOutcome.error.errorResponse?.response?.status
        });
      }
    } catch (error) {
      datadogLogs.logger.error('Failed to save Edit Values changes', {
        message: error instanceof Error ? error.message : String(error)
      });
      saveErrorMessage = SAVE_FAILED_MESSAGE;
    } finally {
      setLoading(false);
    }
    if (saveErrorMessage) {
      setErrors(toServerErrors(saveErrorMessage, keys));
      setSubmitAttempts((attempts) => attempts + 1);
      return;
    }
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const getInitSplit = (initialData: FinancialFields) => {
    if (type === 'asset') {
      const assetData = data as Asset;
      const x2Rate = assetData.x2Rate;
      if (x2Rate !== null && x2Rate !== undefined) return 100 - x2Rate;
      return 100 - (assetData.split1Rate ?? 0) - (assetData.split2Rate ?? 0);
    }
    return initialData.grossMRR ? (Number(initialData.netMRR) / Number(initialData.grossMRR)) * 100 : 100;
  };

  useEffect(() => {
    if (data) {
      setErrors(undefined);
      const initialFinancialData = {} as FinancialFields;
      FINANCIAL_FIELD_NAMES.forEach((key) => {
        initialFinancialData[key] = (data[keys[key] as keyof typeof data] as number) ?? null;
      });

      const split = getInitSplit(initialFinancialData);
      const initialValues: FormDataState = {
        ...initialFinancialData,
        netMRR: getDecimal(Number(initialFinancialData.grossMRR) * (split / 100)),
        netNRR: getDecimal(Number(initialFinancialData.grossNRR) * (split / 100)),
        status: (data[keys.status as keyof typeof data] as string) ?? '',
        actualFirstCommissionRun: (data[keys.actualFirstCommissionRun as keyof typeof data] as string) ?? null,
        actualFirstSpiffRun: (data[keys.actualFirstSpiffRun as keyof typeof data] as string) ?? null,
        actualDecommissionedDate: (data[keys.actualDecommissionedDate as keyof typeof data] as string) ?? null
      };
      updateFormData(initialValues);
      setInitialFormData(initialValues);
      setStoredFinancialData(initialFinancialData);
      setSplit(split);
      // Not persisted anywhere on Asset/ProvisionalAsset -- reset for every edit.
      setRevenueChangeCategory('');
    }
  }, [data, data?.id, open]);

  useEffect(() => {
    if (formData.grossMRR && formData.netMRR && type === 'provisionalAsset') {
      setSplit((Number(formData.netMRR) / Number(formData.grossMRR)) * 100);
    }
  }, [formData.grossMRR]);

  useEffect(() => {
    if (!submitAttempts) return;
    const firstErrorField = contentRef.current?.querySelector('[data-error-field]');
    if (!firstErrorField) return;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    firstErrorField.scrollIntoView?.({ block: 'center', behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    firstErrorField.querySelector<HTMLElement>('input, [role="combobox"]')?.focus({ preventScroll: true });
  }, [submitAttempts]);

  const gridTemplateColumns = data.quoteRepInvolved ? '1fr 1fr 1fr' : '1fr 1fr';
  return (
    <Dialog
      maxWidth={data.quoteRepInvolved ? 'md' : 'sm'}
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (event: React.SubmitEvent) => {
            event.preventDefault();
            handleSave();
          }
        }
      }}
      fullWidth>
      {!loading && <DialogTitle>Edit Values</DialogTitle>}
      <DialogContent
        dividers
        ref={contentRef}>
        {loading && <ComponentSpinner label="Saving..." />}
        {!loading && (
          <Fragment>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplateColumns,
                gap: 4
              }}>
              <Box>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                    <Text
                      size="sm"
                      color="grey6">
                      Commission Status
                    </Text>
                    <SelectEditComponent
                      label="Commissions Status"
                      localVal={formData.status}
                      options={['', ...assetCommissionsStatusOptions]}
                      handleSave={(value) => {
                        setErrors(undefined);
                        updateFormData({ ...formData, status: (value || null) as string | null });
                      }}
                      handleClose={() => {}}
                    />
                  </Box>
                  {keys.expectedFirstCommissionRun && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                      <Text
                        size="sm"
                        color="grey6">
                        Expected First Commission Run
                      </Text>
                      <TextField
                        type="text"
                        size="small"
                        value={formatMonthYear(readOnlyDate('expectedFirstCommissionRun'))}
                        fullWidth
                        disabled
                      />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                    <Text
                      size="sm"
                      color="grey6">
                      Expected Decommission Date
                    </Text>
                    <TextField
                      type="text"
                      size="small"
                      value={formatMonthYear(readOnlyDate('expectedDecommissionDate'))}
                      fullWidth
                      disabled
                    />
                  </Box>
                </Stack>
              </Box>
              <Box>
                <Stack spacing={1}>
                  {keys.serviceStatus && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                      <Text
                        size="sm"
                        color="grey6">
                        Service Status
                      </Text>
                      <TextField
                        type="text"
                        size="small"
                        value={(data as Asset).assetStatus ?? ''}
                        fullWidth
                        disabled
                      />
                    </Box>
                  )}
                  {RUN_DATE_FIELDS.map(([field, label]) => (
                    <Box
                      key={field}
                      sx={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}
                      data-error-field={errors?.[field] ? field : undefined}>
                      <Text
                        size="sm"
                        color="grey6">
                        {label}
                      </Text>
                      <CustomDatePicker
                        value={formData[field]}
                        onChange={handleCommissionDateChange(field)}
                        isMonthYear
                        formatDate="MMM yyyy"
                        error={!!errors?.[field]}
                        errorId={`${field}-error`}
                      />
                      {errors?.[field] && (
                        <FieldError
                          id={`${field}-error`}
                          message={errors[field]}
                        />
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
            <Divider sx={{ mt: 3 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplateColumns,
                gap: 4,
                mt: 2,
                mb: 2
              }}>
              <Box>
                <Stack spacing={1}>
                  <FinancialInputField
                    name="totalMRC"
                    label="Total MRC"
                    value={formData.totalMRC ?? ''}
                    onChange={handleFieldChange}
                    error={!!errors?.totalMRC}
                    helperText={errors?.totalMRC}
                  />
                  <FinancialInputField
                    name="recurringCommissionPercent"
                    label="Recurring Commission %"
                    value={formData.recurringCommissionPercent ?? ''}
                    onChange={handleFieldChange}
                    positionSymbol="end"
                    symbol="%"
                    error={!!errors?.recurringCommissionPercent}
                    helperText={errors?.recurringCommissionPercent}
                  />
                </Stack>
              </Box>
              <Box>
                <Stack spacing={1}>
                  <FinancialInputField
                    name="commissionableMRC"
                    label="Commissionable MRC"
                    value={formData.commissionableMRC ?? ''}
                    onChange={handleFieldChange}
                    error={!!errors?.commissionableMRC}
                    helperText={errors?.commissionableMRC}
                  />
                  <FinancialInputField
                    name="grossMRR"
                    label="Gross MRR"
                    value={formData.grossMRR ?? ''}
                    disabled
                  />
                </Stack>
              </Box>
              {data.quoteRepInvolved && (
                <Box>
                  <Stack spacing={1}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '.3rem'
                      }}>
                      <Text
                        size="sm"
                        color="grey6">
                        Team Selling Discount %
                      </Text>
                      <TextField
                        type="text"
                        size="small"
                        name="quoteTeamSellingDiscount"
                        value={data.quoteTeamSellingDiscount || 0}
                        fullWidth
                        disabled
                        slotProps={{
                          input: {
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                          }
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '.3rem'
                      }}>
                      <Text
                        size="sm"
                        color="grey6">
                        Team Selling Approval Number
                      </Text>
                      <TextField
                        type="text"
                        size="small"
                        name="quoteTeamSellingApprovalNumber"
                        value={data.quoteTeamSellingApprovalNumber}
                        fullWidth
                        disabled
                      />
                    </Box>
                  </Stack>
                </Box>
              )}
            </Box>
            <Divider sx={{ mt: 3 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplateColumns,
                gap: 4,
                mt: 2
              }}>
              <Box>
                <Stack spacing={1}>
                  <FinancialInputField
                    name="nonCommissionableNRC"
                    label="Non Commissionable NRC"
                    value={formData.nonCommissionableNRC ?? ''}
                    onChange={handleFieldChange}
                    error={!!errors?.nonCommissionableNRC}
                    helperText={errors?.nonCommissionableNRC}
                  />
                  <FinancialInputField
                    name="nonRecurringCommissionPercent"
                    label="Non Recurring Commission %"
                    value={formData.nonRecurringCommissionPercent ?? ''}
                    onChange={handleFieldChange}
                    positionSymbol="end"
                    symbol="%"
                    error={!!errors?.nonRecurringCommissionPercent}
                    helperText={errors?.nonRecurringCommissionPercent}
                  />
                </Stack>
              </Box>
              <Box>
                <Stack spacing={1}>
                  <FinancialInputField
                    name="commissionableNRC"
                    label="Commissionable NRC"
                    value={formData.commissionableNRC ?? ''}
                    onChange={handleFieldChange}
                    error={!!errors?.commissionableNRC}
                    helperText={errors?.commissionableNRC}
                  />
                  <FinancialInputField
                    name="grossNRR"
                    label="Gross NRR"
                    value={formData.grossNRR ?? ''}
                    disabled
                  />
                </Stack>
              </Box>
            </Box>
            <Divider sx={{ mt: 3 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplateColumns,
                gap: 4,
                mt: 2
              }}>
              <Box>
                <Stack spacing={1}>
                  <FinancialInputField
                    name="split"
                    label="Split"
                    value={split}
                    disabled
                    symbol="%"
                    positionSymbol="end"
                  />
                </Stack>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplateColumns,
                gap: 4,
                mt: 2
              }}>
              <Box>
                <Stack spacing={1}>
                  <FinancialInputField
                    name="netMRR"
                    label="Net MRR"
                    value={formData.netMRR ?? ''}
                    disabled
                  />
                </Stack>
              </Box>
              <FinancialInputField
                name="netNRR"
                label="Net NRR"
                value={formData.netNRR ?? ''}
                disabled
              />
            </Box>
            <Divider sx={{ mt: 3 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplateColumns,
                gap: 4,
                mt: 2
              }}>
              <Box>
                <Stack spacing={1}>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}
                    data-error-field={errors?.revenueChangeCategory ? 'revenueChangeCategory' : undefined}>
                    <Text
                      size="sm"
                      color="grey6">
                      Revenue Change Category
                      {showCategoryRequired && (
                        <Text
                          isSpan
                          size="sm"
                          color="red"
                          dataTestId="revenueChangeCategory-required">
                          {' *'}
                        </Text>
                      )}
                    </Text>
                    <FormControl
                      size="small"
                      fullWidth
                      error={!!errors?.revenueChangeCategory}>
                      <Select
                        data-testid="revenueChangeCategory-input"
                        value={revenueChangeCategory}
                        onChange={handleRevenueChangeCategoryChange}
                        SelectDisplayProps={{
                          'aria-required': showCategoryRequired,
                          'aria-describedby': errors?.revenueChangeCategory ? 'revenueChangeCategory-error' : undefined
                        }}
                        displayEmpty>
                        <MenuItem value="">
                          <em>Select a category</em>
                        </MenuItem>
                        {REVENUE_CHANGE_CATEGORY_OPTIONS.map((option) => (
                          <MenuItem
                            key={option}
                            value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors?.revenueChangeCategory && (
                      <FieldError
                        id="revenueChangeCategory-error"
                        message={errors.revenueChangeCategory}
                      />
                    )}
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Fragment>
        )}
      </DialogContent>
      {!loading && errors?.form && (
        <Box sx={{ px: 3, pt: 2 }}>
          <FieldError
            id="financialEditForm-error"
            message={errors.form}
          />
        </Box>
      )}
      {!loading && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            data-testid="cancel-button"
            onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={!!errors?.totalMRC}
            type="submit"
            variant="contained">
            Save
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
